import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { QuizQuestion } from "@/types";

interface QuizDisplayProps {
  questions: QuizQuestion[];
}

export function QuizDisplay({ questions }: QuizDisplayProps) {
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [revealedAnswers, setRevealedAnswers] = useState<Set<number>>(new Set());

  const handleSelectOption = (questionIndex: number, option: string) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionIndex]: option }));
    setRevealedAnswers((prev) => {
      const next = new Set(prev);
      next.add(questionIndex);
      return next;
    });
  };

  if (questions.length === 0) {
    return <p className="text-sm text-muted-foreground">No quiz questions available.</p>;
  }

  return (
    <div className="space-y-4">
      {questions.map((question, qIndex) => {
        const isRevealed = revealedAnswers.has(qIndex);
        const selected = selectedAnswers[qIndex];

        return (
          <Card key={qIndex}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {qIndex + 1}. {question.question}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid gap-2">
                {question.options.map((option, oIndex) => {
                  const isCorrect = option === question.correctAnswer;
                  const isSelected = selected === option;

                  let variant: "default" | "outline" | "secondary" | "destructive" = "outline";
                  if (isRevealed) {
                    if (isCorrect) {
                      variant = "default";
                    } else if (isSelected && !isCorrect) {
                      variant = "destructive";
                    } else {
                      variant = "secondary";
                    }
                  }

                  return (
                    <Button
                      key={oIndex}
                      variant={variant}
                      className="h-auto justify-start whitespace-normal text-left"
                      onClick={() => handleSelectOption(qIndex, option)}
                      disabled={isRevealed}
                    >
                      {option}
                    </Button>
                  );
                })}
              </div>
              {isRevealed && (
                <p className="mt-2 text-sm font-medium">
                  {selected === question.correctAnswer ? (
                    <span className="text-green-600">Correct!</span>
                  ) : (
                    <span className="text-destructive">
                      Incorrect. The correct answer is: {question.correctAnswer}
                    </span>
                  )}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
